import React from 'react';
import { DeleteForever, DragIndicator, MoreVert, North, Settings, South, ContentCopyTwoTone } from '@mui/icons-material';
import { Box, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, useTheme, Stack, Paper } from '@mui/material';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSortable } from '@dnd-kit/sortable';

import { DashboardDocument } from "components/resources/dashboards/DashboardDocument";
import { DashboardPageInterface } from 'routes/dashboard/DashboardPage';
import DashboardPageCopyDialog from './DashboardPageCopyDialog';
import ConfirmationDialog from 'components/dialogs/ConfirmationDialog';
import { getDashboardNavigatePath, parsePageParams } from 'components/resources/dashboards/DashboardNavigationUtils';

export interface DashboardPageListRowProps {
  dashboard: DashboardDocument;
  id: string;
  page: DashboardPageInterface;
  activePageId: string | undefined;
  dashboardLink: string;
  closeMenu: () => void;
  handleRefreshPageList: () => void;
  setActivePageEditID: (id: string) => void;
  setNewPageDialogIsOpen: (isOpen: boolean) => void;
  movePageUp: (page:DashboardPageInterface) => void;
  movePageDown: (page:DashboardPageInterface) => void;
  deletePage: (page:DashboardPageInterface) => void;
}

export function DashboardPageListRow(props:DashboardPageListRowProps): JSX.Element {
  const theme = useTheme();
  const {i18n} = useTranslation();

  const { dashboard, page, activePageId, handleRefreshPageList,
    setActivePageEditID, setNewPageDialogIsOpen, movePageUp, movePageDown, deletePage } = props;

  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const menuIsOpen = Boolean(menuAnchorEl);

  const [copyDialogIsOpen, setCopyDialogIsOpen] = React.useState<boolean>(false);
  const [deleteConfirmationDialogIsOpen, setDeleteConfirmationDialogIsOpen] = React.useState<boolean>(false);
  const [activePage, /* setActivePage */] = React.useState<DashboardPageInterface>(page);

  const isSelected = (page.id === activePageId);

  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: props.id });

  const draggingStyle = {
    cursor: "pointer",
    transition,
    ...(isDragging &&
      {
        background: theme.palette.primary.light,
        zIndex: 10
      }
    ),
    ...(transform &&
      {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`
      }
    )
  };

  const pageParams = parsePageParams();
  pageParams.pageId = page.id;
  const navigatePath = getDashboardNavigatePath(pageParams);

  return (
    <>
      <Paper
        key={page.id}
        ref={setNodeRef}
        style={draggingStyle}
      >
        <Stack
          direction="row"
          {...attributes}
          {...listeners}
          sx={[{
            alignItems: "center",
            gap: 1,
            justifyContent: "flex-start",
            background: isSelected ? theme.colors.primary.light : theme.palette.background.paper,
            borderRadius: theme.general.borderRadius,
            touchAction: "none",
            zIndex: 1
          }, ...(Array.isArray(listeners.sx) ? listeners.sx : [listeners.sx])]}>
          <DragIndicator
            sx={{
              color: isSelected ? theme.colors.primary.dark : theme.palette.grey[400],
              marginLeft: 1,
            }}
          />
          <Box
            component={Link}
            to={navigatePath}
            onClick={() => props.closeMenu()}
            sx={{
              textDecoration: "none",
              fontWeight: "bold",
              color: theme.palette.common.black,
              pointerEvents: isDragging ? "none" : "auto",
              flexGrow: 1
            }}
          >
            {page.name}
          </Box>
          <IconButton
            sx={{ marginLeft: "auto" }}
            onClick={(event) => setMenuAnchorEl(event.currentTarget)}
          >
            <MoreVert />
          </IconButton>
        </Stack>
      </Paper>
      {dashboard?._modifiable &&
        <Menu
          className="drag-cancel"
          anchorEl={menuAnchorEl}
          open={menuIsOpen}
          sx={{p:10}}
          onClose={() => setMenuAnchorEl(null)}
        >
          <MenuItem onClick={() => {
            setActivePageEditID(page.id);
            setNewPageDialogIsOpen(true);
            setMenuAnchorEl(null);
          }}>
            <ListItemIcon><Settings/></ListItemIcon>
            <ListItemText>{i18n.t("dashboard.edit-pages.open-page-settings")}</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => {
            movePageUp(page);
            setMenuAnchorEl(null);
          }}>
            <ListItemIcon><North/></ListItemIcon>
            <ListItemText>{i18n.t("dashboard.edit-pages.move-up")}</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => {
            movePageDown(page);
            setMenuAnchorEl(null);
          }}>
            <ListItemIcon><South/></ListItemIcon>
            <ListItemText>{i18n.t("dashboard.edit-pages.move-down")}</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => {
            setCopyDialogIsOpen(true);
            setMenuAnchorEl(null);
          }}>
            <ListItemIcon><ContentCopyTwoTone/></ListItemIcon>
            <ListItemText>{i18n.t("dashboard.edit-pages.duplicate")}</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => {
            setDeleteConfirmationDialogIsOpen(true);
            setMenuAnchorEl(null);
          }}>
            <ListItemIcon><DeleteForever sx={{ color: theme.palette.error.main }}/></ListItemIcon>
            <ListItemText>{i18n.t("delete")}</ListItemText>
          </MenuItem>
        </Menu>
      }
      {copyDialogIsOpen &&
        <DashboardPageCopyDialog
          dashboard={dashboard}
          page={page}
          onClose={() => {
            setCopyDialogIsOpen(false);
            handleRefreshPageList();
          }}
        />
      }
      {deleteConfirmationDialogIsOpen &&
        <ConfirmationDialog
          isOpen={deleteConfirmationDialogIsOpen}
          title={i18n.t("dashboard.edit-pages.delete-confirmation-title")}
          description={i18n.t("dashboard.edit-pages.delete-confirmation-description")}
          onClose={(isConfirmed:boolean) => {
            if (isConfirmed) {
              deletePage(activePage);
            }
            setDeleteConfirmationDialogIsOpen(false);
          }}
        />
      }
    </>
  );
}
